import { CloseOutlined } from "@ant-design/icons";
import { faCaretDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "antd";
import mergeClassNames from "common/mergeClassNames";
import React from "react";

interface ISlideMenuGuideProps {
    open?: boolean;
    position?: { x: number; y: number };
    onClose?: () => void;
    zIndex?: number;
}

/**
 * Reusable Hook for setting the animation of slide menu guide
 */
export function useSlideMenuGuide(elementClassName: string, secondaryEleClassName: string) {
    const [guidePostion, setGuidePostion] = React.useState<{ x: number; y: number } | undefined>();
    const [isReady, setIsReady] = React.useState<boolean>(false);

    const getGuidePostion = React.useCallback(() => {
        const element = document.getElementsByClassName(elementClassName)[0];
        const pos = element ? element.getBoundingClientRect() : { height: 0, left: 0 };

        const secondaryEle = document.getElementsByClassName(secondaryEleClassName)[0];

        const firstCell = secondaryEle ? (secondaryEle as Element).querySelector("td") : (element as Element).querySelector("th");
        const firstCellRect = firstCell ? firstCell.getBoundingClientRect() : null;
        const newPos = {
            x: 0 + (firstCellRect ? firstCellRect.width / 2 - 6 : 4), // Align to the left edge of the table
            y: pos.height / 2 - 16, // center of table header; 16 is half the height of the slider menu
        };
        setGuidePostion(newPos);
    }, [elementClassName, secondaryEleClassName]);

    React.useEffect(() => {
        // Wait for header to render
        const timeout = window.setInterval(() => {
            const element = document.getElementsByClassName(elementClassName)[0];
            const secondaryEle = document.getElementsByClassName(secondaryEleClassName)[0];
            if (element && secondaryEle) {
                getGuidePostion();
                clearInterval(timeout);
            }
        }, 300);

        return () => {
            window.clearInterval(timeout);
            setGuidePostion(undefined);
        };
    }, [elementClassName, getGuidePostion, secondaryEleClassName]);

    React.useEffect(() => {
        if (guidePostion) {
            // Give sometime to set the positions
            const showTimeout = window.setTimeout(() => {
                setIsReady(true);
                window.clearTimeout(showTimeout);
            }, 100);
            // Close it after 4s
            const closeTimeout = window.setTimeout(() => {
                setIsReady(false);
                window.clearTimeout(closeTimeout);
            }, 4000);
        }

        return () => {
            setIsReady(false);
        };
    }, [guidePostion]);

    const memoed = React.useMemo(
        () => ({
            isReady,
            guidePostion,
        }),
        [isReady, guidePostion],
    );

    return memoed;
}

export default function SlideMenuGuide({ open = false, position, onClose, zIndex = 0 }: ISlideMenuGuideProps) {
    const [isReady, setIsReady] = React.useState<boolean>(false);

    React.useEffect(() => {
        setIsReady(true);

        return () => setIsReady(false);
    }, []);

    return (
        <div
            className={mergeClassNames(
                "absolute z-[29] flex flex-row items-center justify-between gap-2 rounded-sm bg-amber-200 px-3 py-1 transition-all duration-700",
                open ? "opacity-100" : "!-left-96 opacity-0",
            )}
            style={
                position
                    ? {
                          top: position.y,
                          left: position.x,
                          zIndex: 29 + zIndex,
                      }
                    : undefined
            }
        >
            <FontAwesomeIcon className="absolute -bottom-6 left-0 animate-bounce text-xl text-amber-500" icon={faCaretDown} />
            <span>Use this menu for more options.</span>
            <Button type="text" size="small" icon={<CloseOutlined className="text-amber-500" />} onClick={onClose} />
            <div
                className={mergeClassNames("absolute bottom-0 left-0 h-[2px] rounded-sm bg-amber-300 transition-all duration-[4000ms] ease-linear", isReady ? "w-full" : "w-0")}
            ></div>
        </div>
    );
}
